import { loadModules } from 'esri-loader';
import { DEFAULT_SHAPE_COLORS } from './constant';

// Utility functions
const utils = {
  haversineDistance: (coord1, coord2) => {
    try {
      const R = 6371000;
      const lat1 = (coord1[1] * Math.PI) / 180;
      const lat2 = (coord2[1] * Math.PI) / 180;
      const deltaLat = ((coord2[1] - coord1[1]) * Math.PI) / 180;
      const deltaLon = ((coord2[0] - coord1[0]) * Math.PI) / 180;

      const a =
        Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
        Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLon / 2) * Math.sin(deltaLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

      return R * c;
    } catch (error) {
      console.error('Haversine calculation error:', error);
      return 0;
    }
  },

  planarDistance: (coord1, coord2) => {
    try {
      const dx = coord2[0] - coord1[0];
      const dy = coord2[1] - coord1[1];
      return Math.sqrt(dx * dx + dy * dy);
    } catch (error) {
      console.error('Planar calculation error:', error);
      return 0;
    }
  },

  calculateDistance: async (point1, point2, view) => {
    try {
      const [geometryEngine] = await loadModules(['esri/geometry/geometryEngine']);

      const pt1 = {
        type: 'point',
        x: point1[0],
        y: point1[1],
        spatialReference: view.spatialReference,
      };
      const pt2 = {
        type: 'point',
        x: point2[0],
        y: point2[1],
        spatialReference: view.spatialReference,
      };

      if (geometryEngine.geodesicDistance) {
        const distance = geometryEngine.geodesicDistance(pt1, pt2, 'meters');
        if (distance > 0) return Math.abs(distance);
      }

      if (geometryEngine.distance) {
        const distance = geometryEngine.distance(pt1, pt2, 'meters');
        if (distance > 0) return Math.abs(distance);
      }
    } catch (error) {
      console.warn('ArcGIS distance calculation failed:', error);
    }

    if (view.spatialReference.isGeographic || view.spatialReference.wkid === 4326) {
      return utils.haversineDistance(point1, point2);
    }

    const planarDist = utils.planarDistance(point1, point2);
    return view.spatialReference.isGeographic ? planarDist * 111320 : planarDist;
  },

  calculateArea: async (geometry, view) => {
    if (geometry.type !== 'polygon') return 0;

    try {
      const [geometryEngine] = await loadModules(['esri/geometry/geometryEngine']);

      if (geometryEngine.geodesicArea) {
        const area = geometryEngine.geodesicArea(geometry, 'square-meters');
        if (area > 0) return Math.abs(area);
      }

      if (geometryEngine.planarArea) {
        const area = geometryEngine.planarArea(geometry, 'square-meters');
        if (area > 0) return Math.abs(area);
      }
    } catch (error) {
      console.warn('ArcGIS area calculation failed:', error);
    }

    const rings = geometry.rings[0];
    if (!rings || rings.length < 3) return 0;

    let area = 0;
    for (let i = 0; i < rings.length - 1; i++) {
      area += rings[i][0] * rings[i + 1][1];
      area -= rings[i + 1][0] * rings[i][1];
    }
    area = Math.abs(area) / 2;

    return view.spatialReference.isGeographic ? area * 12391399000 : area;
  },

  formatDistance: (meters) => {
    if (meters < 1) return `${(meters * 100).toFixed(1)} cm`;
    if (meters < 1000) return `${meters.toFixed(2)} m`;
    return `${(meters / 1000).toFixed(3)} km`;
  },

  formatArea: (sqMeters) => {
    if (sqMeters < 1) return `${(sqMeters * 10000).toFixed(2)} cm²`;
    if (sqMeters < 10000) return `${sqMeters.toFixed(2)} m²`;
    return `${(sqMeters / 10000).toFixed(2)} ha`;
  },

  calculateCentroid: (geometry) => {
    if (!geometry) return null;

    switch (geometry.type) {
      case 'polygon':
        if (geometry.centroid) return geometry.centroid;

        const rings = geometry.rings[0];
        if (!rings?.length) return null;

        const x = rings.reduce((sum, ring) => sum + ring[0], 0) / rings.length;
        const y = rings.reduce((sum, ring) => sum + ring[1], 0) / rings.length;

        return { type: 'point', x, y, spatialReference: geometry.spatialReference };

      case 'polyline':
        const paths = geometry.paths[0];
        if (!paths?.length) return null;

        const midIndex = Math.floor(paths.length / 2);
        return {
          type: 'point',
          x: paths[midIndex][0],
          y: paths[midIndex][1],
          spatialReference: geometry.spatialReference,
        };

      case 'point':
        return geometry;

      default:
        return geometry.extent?.center || null;
    }
  },

  calculateMidpoint: (coord1, coord2, view) => ({
    type: 'point',
    x: (coord1[0] + coord2[0]) / 2,
    y: (coord1[1] + coord2[1]) / 2,
    spatialReference: view.spatialReference,
  }),

  getFontSize: (zoomLevel) => {
    if (zoomLevel < 14) return 0;
    if (zoomLevel < 16) return 9;
    if (zoomLevel < 18) return 10;
    if (zoomLevel < 20) return 11;
    return 12;
  },

  getShapeTypeColor: (index) => DEFAULT_SHAPE_COLORS[index % DEFAULT_SHAPE_COLORS.length],

  extractCoordinates: (geometry) => {
    switch (geometry.type) {
      case 'polygon':
        return geometry.rings;
      case 'polyline':
        return geometry.paths;
      case 'point':
        return [geometry.x, geometry.y];
      default:
        return null;
    }
  },

  // Create geometry from stored coordinates and spatial reference
  recreateGeometry: async (storedGeometry, view) => {
    const [Polygon, Polyline, Point] = await loadModules([
      'esri/geometry/Polygon',
      'esri/geometry/Polyline',
      'esri/geometry/Point',
    ]);
    console.log(storedGeometry, 'storedGeometry to recreate');
    try {
      const spatialReference = view.spatialReference;

      switch (storedGeometry.tool) {
        case 'polygon':
          return new Polygon({
            rings: storedGeometry.geometry.rings,
            spatialReference: spatialReference,
          });

        case 'polyline':
          return new Polyline({
            paths: storedGeometry.geometry.paths,
            spatialReference: spatialReference,
          });

        case 'point':
          return new Point({
            x: storedGeometry.x,
            y: storedGeometry.y,
            spatialReference: spatialReference,
          });

        default:
          console.error('Unknown geometry type:', storedGeometry.tool);
          return null;
      }
    } catch (error) {
      console.error('Error recreating geometry:', error);
      return null;
    }
  },
};

export default utils;
